import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PlayButtonLight } from '@onesource/icons';
import type { TContentBlockBase, TLessonBase } from '@onesource/schemas';
import { ContentBlockResourceType, ContentBlockType } from '@onesource/schemas';
import DOMPurify from 'dompurify';
import { AiOutlineMessage } from 'react-icons/ai';
import { LuInfo } from 'react-icons/lu';
import { MdFolder, MdOutlineQuiz } from 'react-icons/md';

import { blockResourceMeta, RESOURCE_TYPE_META } from './types';

// Returns false for empty rich-text values like "<p><br></p>" or "&nbsp;"
// that are truthy but render nothing.
const hasMeaningfulHtml = (html?: string | null) => {
  if (!html) {
    return false;
  }
  const text = DOMPurify.sanitize(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return text.length > 0;
};

interface LessonContentSummaryProps {
  lesson: TLessonBase;
  hasSubLessons: boolean;
  videoBlocks: TContentBlockBase[];
  documentBlocks: TContentBlockBase[];
  quizBlocks: TContentBlockBase[];
  onNavigateToBlock: (block: TContentBlockBase) => void;
}

export const LessonContentSummary = ({
  lesson,
  hasSubLessons,
  videoBlocks,
  documentBlocks,
  quizBlocks,
  onNavigateToBlock,
}: LessonContentSummaryProps) => {
  const theme = useTheme();

  const hasDiscussionTitle = hasMeaningfulHtml(lesson.communityDiscussionTitle);
  const hasAdditionalResources = hasMeaningfulHtml(lesson.additionalResources);

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '15px', border: '1px solid #f8e8e4', p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 400, mb: 1, fontSize: '12px !important', letterSpacing: '23%', color: '#AAAAAA' }}
      >
        IN THIS LESSON
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, color: theme.palette.common.black }}>
        {hasSubLessons && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MdFolder size={12} color={theme.palette.primary.main} />
            <Typography variant="caption" sx={{ fontSize: '12px' }}>
              <strong>Sub-lessons:</strong> {lesson.subLessonCount || lesson.children?.length || 0} lessons to complete
            </Typography>
          </Box>
        )}

        {!hasSubLessons &&
          videoBlocks.map((block, idx) => {
            const label = RESOURCE_TYPE_META[block.resourceType ?? ContentBlockResourceType.VIDEO]?.label;
            return (
              <Box
                key={block.id}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, cursor: 'pointer' }}
                onClick={() => onNavigateToBlock(block)}
              >
                <PlayButtonLight fontSize={12} color={theme.palette.primary.main} style={{ marginTop: 3 }} />
                <Typography variant="caption" sx={{ fontSize: '12px' }}>
                  <strong>{label}:</strong> {block.title || `${label} ${idx + 1}`}
                  {block.vimeoDurationSeconds && ` (${Math.ceil(block.vimeoDurationSeconds / 60)} min)`}
                </Typography>
              </Box>
            );
          })}

        {!hasSubLessons &&
          documentBlocks.map((block, idx) => {
            const isPdf = block.blockType === ContentBlockType.PDF;
            const isRedirect = block.blockType === ContentBlockType.REDIRECT;
            // The block's own icon and wording — the same ones its card shows.
            const { icon: Icon, label } = blockResourceMeta(block);
            const fallbackTitle = isPdf ? block.pdfFilename : undefined;

            const handleClick = () => {
              if (isRedirect) {
                if (block.redirectUrl) {
                  window.open(block.redirectUrl, '_blank', 'noopener,noreferrer');
                }
                return;
              }
              onNavigateToBlock(block);
            };

            return (
              <Box
                key={block.id}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, cursor: 'pointer' }}
                onClick={handleClick}
              >
                <Icon size={12} color={theme.palette.primary.main} style={{ marginTop: 2 }} />
                <Typography variant="caption" sx={{ fontSize: '12px', lineHeight: '17px', width: 'calc(100% - 20px)' }}>
                  <strong>{label}:</strong> {block.title || fallbackTitle || `${label} ${idx + 1}`}
                </Typography>
              </Box>
            );
          })}

        {!hasSubLessons &&
          quizBlocks.map(block => {
            const label = RESOURCE_TYPE_META[block.resourceType ?? ContentBlockResourceType.QUIZ]?.label;
            return (
              <Box
                key={block.id}
                sx={{ display: 'flex', gap: 0.5, cursor: 'pointer' }}
                onClick={() => onNavigateToBlock(block)}
              >
                <MdOutlineQuiz size={12} color={theme.palette.primary.main} style={{ marginTop: 3 }} />
                <Typography variant="caption" sx={{ fontSize: '12px' }}>
                  <strong>{label}:</strong> {block.title || 'Check your understanding'}
                </Typography>
              </Box>
            );
          })}

        {hasDiscussionTitle && (
          <Box sx={{ display: 'flex', gap: 0.5, cursor: 'pointer' }}>
            <AiOutlineMessage size={12} color={theme.palette.primary.main} style={{ marginTop: 3 }} />
            <Typography variant="caption" sx={{ fontSize: '12px', width: 'calc(100% - 20px)' }} component="div">
              <Box
                component="span"
                sx={{
                  '& a': { textDecoration: 'none', color: theme.palette.common.black },
                  '& p': { display: 'inline', m: 0 },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(lesson.communityDiscussionTitle ?? ''),
                }}
              />
            </Typography>
          </Box>
        )}

        {hasAdditionalResources && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <LuInfo size={12} color={theme.palette.primary.main} style={{ marginTop: 3 }} />
            <Typography variant="caption" sx={{ fontSize: '12px', width: 'calc(100% - 20px)' }} component="div">
              <Box
                component="span"
                sx={{
                  '& a': { textDecoration: 'none', color: theme.palette.common.black },
                  '& p': { display: 'inline', m: 0 },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(lesson.additionalResources ?? ''),
                }}
              />
            </Typography>
          </Box>
        )}

        {!hasSubLessons &&
          videoBlocks.length === 0 &&
          documentBlocks.length === 0 &&
          quizBlocks.length === 0 &&
          !hasDiscussionTitle &&
          !hasAdditionalResources && (
            <Typography variant="caption" color="text.secondary">
              No content items listed
            </Typography>
          )}
      </Box>
    </Box>
  );
};
